/**
 * F3 Frame + F7 Shadow/Glow — SVG-based client handlers.
 *
 * Branches on `intent.id`:
 *   - F3 → frame placement (thin-line / wood / vintage / circular)
 *   - F7 → shadow / glow filter via SVG <filter>
 *
 * Both share `clientSvgHandler` (matches DISPATCH_MATRIX `client_svg`).
 */

import { registerClientHandler } from './registry.js'
import { loadImage } from '../utils/mockup-composite.js'

let _activeListeners = []
let _activeRoot = null

function track(target, event, fn) {
  target.addEventListener(event, fn)
  _activeListeners.push(() => target.removeEventListener(event, fn))
}

function cleanup() {
  while (_activeListeners.length) {
    try { _activeListeners.pop()() } catch {}
  }
  _activeRoot = null
}

// ---- Public handler ----

export const clientSvgHandler = {
  mountControls(rootEl, ctx) {
    cleanup()
    _activeRoot = rootEl
    if (ctx.intent?.id === 'F7') return mountShadowUI(rootEl, ctx)
    return mountFrameUI(rootEl, ctx)
  },

  unmount() {
    cleanup()
    if (_activeRoot) _activeRoot.innerHTML = ''
  },

  async run(ctx) {
    if (ctx.intent?.id === 'F7') {
      const dataUrl = await composeShadowGlow(ctx.image, {
        effect: 'shadow', color: '#000', blur: 16, offsetX: 8, offsetY: 8, opacity: 0.5,
      })
      return { result_data_url: dataUrl, metadata: { effect: 'shadow' } }
    }
    const dataUrl = await composeFrame(ctx.image, { id: 'thin-line', thickness: 16, color: '#000' })
    return { result_data_url: dataUrl, metadata: { frame: 'thin-line' } }
  },
}

registerClientHandler('clientSvgHandler', clientSvgHandler)

// ---- F3 Frame ----

function mountFrameUI(rootEl, ctx) {
  const frames = ctx.config?.frames || ['thin-line', 'wood', 'vintage', 'circular']
  let active = { id: frames[0], thickness: 16, color: '#000000' }

  rootEl.innerHTML = `
    <div class="ctt-frame">
      <div class="ctt-controls">
        <label>Frame:</label>
        ${frames.map(f => `
          <button type="button" class="ctt-pill ${f === active.id ? 'active' : ''}" data-frame="${f}">${f}</button>
        `).join('')}
      </div>
      <div class="ctt-controls">
        <label for="frameThickness">Thickness</label>
        <input type="range" id="frameThickness" min="1" max="64" value="${active.thickness}" />
        <span class="ctt-value" data-bind="thickness">${active.thickness}px</span>
        <label for="frameColor">Color</label>
        <input type="color" id="frameColor" value="${active.color}" />
      </div>
    </div>
  `

  rootEl.querySelectorAll('[data-frame]').forEach(btn => {
    track(btn, 'click', () => {
      active.id = btn.dataset.frame
      rootEl.querySelectorAll('[data-frame]').forEach(b => b.classList.toggle('active', b === btn))
      emit()
    })
  })

  const thicknessEl = rootEl.querySelector('#frameThickness')
  const colorEl = rootEl.querySelector('#frameColor')
  const thicknessLabel = rootEl.querySelector('[data-bind="thickness"]')
  track(thicknessEl, 'input', () => {
    active.thickness = Number(thicknessEl.value)
    if (thicknessLabel) thicknessLabel.textContent = `${active.thickness}px`
    emitDebounced()
  })
  track(colorEl, 'input', () => {
    active.color = colorEl.value
    emitDebounced()
  })

  let emitTimer = null
  function emitDebounced() {
    clearTimeout(emitTimer)
    emitTimer = setTimeout(emit, 60)
  }
  async function emit() {
    try {
      const dataUrl = await composeFrame(ctx.image, active)
      ctx.onResult?.({
        result_data_url: dataUrl,
        metadata: { frame: active.id, thickness: active.thickness, color: active.color },
      })
    } catch (err) {
      ctx.onError?.(err)
    }
  }

  emit()
}

async function composeFrame(imageUrl, frame) {
  const img = await loadImage(imageUrl)
  const t = frame.thickness
  const canvas = document.createElement('canvas')
  canvas.width = img.naturalWidth + t * 2
  canvas.height = img.naturalHeight + t * 2
  const c = canvas.getContext('2d')

  drawFrameBackground(c, frame, canvas.width, canvas.height)
  c.drawImage(img, t, t)

  if (frame.id === 'circular') {
    return clipToCircle(canvas, t)
  }
  return canvas.toDataURL('image/png')
}

// Image-content colors, not themed UI chrome — frame defaults (#000) and the
// wood/vintage brown palette below are drawn into the exported image pixels
// and must not follow the theme.
function drawFrameBackground(c, frame, w, h) {
  switch (frame.id) {
    case 'thin-line':
      c.fillStyle = frame.color
      c.fillRect(0, 0, w, h)
      break
    case 'wood': {
      const grad = c.createLinearGradient(0, 0, w, h)
      grad.addColorStop(0, '#8a5a2a')
      grad.addColorStop(0.5, '#6b4520')
      grad.addColorStop(1, '#8a5a2a')
      c.fillStyle = grad
      c.fillRect(0, 0, w, h)
      break
    }
    case 'vintage': {
      c.fillStyle = '#1a1410'
      c.fillRect(0, 0, w, h)
      // Inner double-line
      c.strokeStyle = '#caa66a'
      c.lineWidth = Math.max(1, frame.thickness * 0.08)
      c.strokeRect(frame.thickness * 0.4, frame.thickness * 0.4, w - frame.thickness * 0.8, h - frame.thickness * 0.8)
      break
    }
    case 'circular':
      c.fillStyle = frame.color
      c.fillRect(0, 0, w, h)
      break
    default:
      c.fillStyle = frame.color
      c.fillRect(0, 0, w, h)
  }
}

function clipToCircle(srcCanvas, padding) {
  const out = document.createElement('canvas')
  out.width = srcCanvas.width
  out.height = srcCanvas.height
  const c = out.getContext('2d')
  c.beginPath()
  c.arc(out.width / 2, out.height / 2, Math.min(out.width, out.height) / 2 - padding, 0, Math.PI * 2)
  c.closePath()
  c.clip()
  c.drawImage(srcCanvas, 0, 0)
  return out.toDataURL('image/png')
}

// ---- F7 Shadow / Glow ----

function mountShadowUI(rootEl, ctx) {
  const effects = ctx.config?.effects || ['shadow', 'glow']
  let params = {
    effect: effects[0],
    color: '#000000',
    blur: 16,
    offsetX: 8,
    offsetY: 8,
    opacity: 0.5,
  }

  rootEl.innerHTML = `
    <div class="ctt-shadow">
      <div class="ctt-controls">
        <label>Effect:</label>
        ${effects.map(e => `
          <button type="button" class="ctt-pill ${e === params.effect ? 'active' : ''}" data-effect="${e}">${e}</button>
        `).join('')}
      </div>
      <div class="ctt-controls">
        <label>Color</label> <input type="color" id="fxColor" value="${params.color}" />
        <label>Blur</label>  <input type="range" id="fxBlur"   min="0"  max="64"   value="${params.blur}" />
        <label>X</label>     <input type="range" id="fxOffsetX" min="-32" max="32" value="${params.offsetX}" />
        <label>Y</label>     <input type="range" id="fxOffsetY" min="-32" max="32" value="${params.offsetY}" />
        <label>Opacity</label><input type="range" id="fxOpacity" min="0" max="100" value="${Math.round(params.opacity * 100)}" />
      </div>
    </div>
  `

  rootEl.querySelectorAll('[data-effect]').forEach(btn => {
    track(btn, 'click', () => {
      params.effect = btn.dataset.effect
      rootEl.querySelectorAll('[data-effect]').forEach(b => b.classList.toggle('active', b === btn))
      emit()
    })
  })

  const bind = (id, key, transform = Number) => {
    const el = rootEl.querySelector(`#${id}`)
    if (!el) return
    track(el, 'input', () => {
      params[key] = transform(el.value)
      emitDebounced()
    })
  }
  bind('fxColor', 'color', String)
  bind('fxBlur', 'blur')
  bind('fxOffsetX', 'offsetX')
  bind('fxOffsetY', 'offsetY')
  bind('fxOpacity', 'opacity', (v) => Number(v) / 100)

  let emitTimer = null
  function emitDebounced() {
    clearTimeout(emitTimer)
    emitTimer = setTimeout(emit, 80)
  }
  async function emit() {
    try {
      const dataUrl = await composeShadowGlow(ctx.image, params)
      ctx.onResult?.({
        result_data_url: dataUrl,
        metadata: { ...params },
      })
    } catch (err) {
      ctx.onError?.(err)
    }
  }
  emit()
}

async function composeShadowGlow(imageUrl, params) {
  const img = await loadImage(imageUrl)
  const filterId = 'fx_' + Math.random().toString(36).slice(2)
  const fx = params.effect === 'shadow'
    ? `<feDropShadow dx="${params.offsetX}" dy="${params.offsetY}" stdDeviation="${params.blur}" flood-color="${params.color}" flood-opacity="${params.opacity}"/>`
    : `<feGaussianBlur stdDeviation="${params.blur}" result="b"/>
       <feFlood flood-color="${params.color}" flood-opacity="${params.opacity}"/>
       <feComposite in2="b" operator="in"/>
       <feMerge><feMergeNode/><feMergeNode in="SourceGraphic"/></feMerge>`

  const padX = Math.max(0, Math.abs(params.offsetX)) + params.blur * 2
  const padY = Math.max(0, Math.abs(params.offsetY)) + params.blur * 2

  // Inline image bytes via data URL to avoid CORS issues with SVG <image href>
  const dataSrc = await imageToDataUrl(img)

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${img.naturalWidth + padX * 2}" height="${img.naturalHeight + padY * 2}">
    <defs>
      <filter id="${filterId}" x="-25%" y="-25%" width="150%" height="150%">${fx}</filter>
    </defs>
    <image href="${dataSrc}" x="${padX}" y="${padY}" width="${img.naturalWidth}" height="${img.naturalHeight}" filter="url(#${filterId})" />
  </svg>`

  const blob = new Blob([svg], { type: 'image/svg+xml' })
  const url = URL.createObjectURL(blob)
  try {
    const rendered = await loadImage(url)
    const canvas = document.createElement('canvas')
    canvas.width = img.naturalWidth + padX * 2
    canvas.height = img.naturalHeight + padY * 2
    canvas.getContext('2d').drawImage(rendered, 0, 0)
    return canvas.toDataURL('image/png')
  } finally {
    URL.revokeObjectURL(url)
  }
}

async function imageToDataUrl(img) {
  const canvas = document.createElement('canvas')
  canvas.width = img.naturalWidth
  canvas.height = img.naturalHeight
  canvas.getContext('2d').drawImage(img, 0, 0)
  return canvas.toDataURL('image/png')
}
